import PropTypes from 'prop-types';
import { m, AnimatePresence } from 'framer-motion';
// @mui
import { alpha } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
// utils
import { fData } from 'src/utils/format-number';
//
import { Tooltip } from '@mui/material';
import Iconify from '../iconify';
import { varFade } from '../animate';
import FileThumbnail, { fileData } from '../file-thumbnail';

// ----------------------------------------------------------------------

export default function MultiFilePreview({
  thumbnail,
  files,
  onRemove,
  sx,
  onSingleDownload,
  currentUser,
}) {
  return (
    <AnimatePresence initial={false}>
      {files?.map((file) => {
        const { key, name = '', size = 0 } = fileData(file);

        const isNotFormatFile = typeof file === 'string';

        let displayName = name;

        if (isNotFormatFile) {
          const lastSlashIndexWin = name.lastIndexOf('\\');
          const lastSlashIndexUnix = name.lastIndexOf('/');
          const lastSlashIndex = Math.max(lastSlashIndexWin, lastSlashIndexUnix);

          if (lastSlashIndex > -1) {
            displayName = name.substring(lastSlashIndex + 1);
          } else {
            displayName = name;
          }
        }

        if (thumbnail) {
          return (
            <Stack
              key={key}
              component={m.div}
              {...varFade().inUp}
              alignItems="center"
              display="inline-flex"
              justifyContent="center"
              sx={{
                m: 0.5,
                width: 80,
                height: 80,
                borderRadius: 1.25,
                overflow: 'hidden',
                position: 'relative',
                border: (theme) => `solid 1px ${alpha(theme.palette.grey[500], 0.16)}`,
                ...sx,
              }}
            >
              <FileThumbnail
                tooltip
                imageView
                file={file} // Pass the original file object for thumbnail generation
                sx={{ position: 'absolute' }}
                imgSx={{ position: 'absolute' }}
              />

              {onRemove && (
                <IconButton
                  size="small"
                  onClick={() => onRemove(file)}
                  sx={{
                    p: 0.5,
                    top: 4,
                    right: 2,
                    position: 'absolute',
                    color: 'common.white',
                    bgcolor: (theme) => alpha(theme.palette.grey[900], 0.48),
                    '&:hover': {
                      bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72),
                    },
                  }}
                >
                  <Iconify icon="mingcute:close-line" width={14} />
                </IconButton>
              )}

              {onSingleDownload && (
                <Tooltip title="Download Single File" arrow placement="top">
                  <IconButton size="small" onClick={() => onSingleDownload(file)}>
                    <Iconify
                      icon="charm:download"
                      width="40"
                      height="40"
                      style={{ color: 'Green' }}
                    />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          );
        }

        return (
          <Stack
            key={key}
            component={m.div}
            {...varFade().inUp}
            spacing={2}
            direction="row"
            alignItems="center"
            sx={{
              my: 1,
              py: 1,
              px: 1.5,
              borderRadius: 1,
              border: (theme) => `solid 1px ${alpha(theme.palette.grey[500], 0.16)}`,
              ...sx,
            }}
          >
            <FileThumbnail file={file} />

            <ListItemText
              primary={displayName}
              secondary={isNotFormatFile ? '' : fData(size)}
              secondaryTypographyProps={{
                component: 'span',
                typography: 'caption',
              }}
            />

            {onRemove && (
              <Tooltip title="Remove Single File" arrow placement="top">
                <IconButton size="small" onClick={() => onRemove(file)}>
                  <Iconify icon="mingcute:close-line" width={16} style={{ color: 'red' }} />
                </IconButton>
              </Tooltip>
            )}

            {onSingleDownload && (
              <Tooltip title="Download Single File" arrow placement="top">
                <IconButton size="small" onClick={() => onSingleDownload(file)}>
                  <Iconify
                    icon="charm:download"
                    width="30"
                    height="30"
                    style={{ color: 'Green' }}
                  />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        );
      })}
    </AnimatePresence>
  );
}

MultiFilePreview.propTypes = {
  files: PropTypes.array,
  onRemove: PropTypes.func,
  sx: PropTypes.object,
  thumbnail: PropTypes.bool,
  onSingleDownload: PropTypes.func,
  currentUser: PropTypes.object,
};
